import React, { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import Dialog from "@/src/components/dialog/Dialog";
import { SwitchField } from "@/src/components/form-fields/SwitchField";
import { TextField } from "@/src/components/form-fields/TextField";
import { Button } from "@/src/components/ui/button";
import { useStorageAccessMutations } from "@/src/hooks/mutations/useStorageAccessMutations";
import { cn } from "@/src/lib/utils";
import { applyApiFieldErrors } from "@/src/utils/applyApiFieldErrors";

import ProviderIcon from "./ProviderIcon";
import { buildStorageAccessSchema } from "./storageAccessSchema";

import type { JSX } from "react";

import type {
  StorageAccess,
  StorageAccessPayload,
  StorageProviderKind,
} from "@/src/api/ProjectAPI.types";

import type { StorageAccessFormValues } from "./storageAccessSchema";

type TranslateFn = (key: string) => string;

const API_FIELD_MAP: Record<string, keyof StorageAccessFormValues> = {
  label: "label",
  bucket_url: "bucket",
  region: "region",
  host: "host",
  access_key: "access_key_id",
  secret_key: "secret_access_key",
};

function translateFieldError(
  field: string,
  serverMessage: string,
  t: TranslateFn,
): string {
  if (field === "bucket_url") return t("storageAccess.form.errors.bucketUrl");
  if (field === "host") return t("storageAccess.form.errors.hostRequired");
  if (field === "label" && serverMessage.includes("taken")) {
    return t("storageAccess.form.errors.labelTaken");
  }
  return serverMessage;
}

function buildPayload(
  values: StorageAccessFormValues,
  provider: StorageProviderKind,
): StorageAccessPayload {
  return {
    label: values.label,
    type: provider,
    bucket_url: values.bucket,
    region: values.region,
    host: values.host.trim() === "" ? null : values.host,
    access_key: values.access_key_id,
    secret_key:
      values.secret_access_key.trim() === ""
        ? undefined
        : values.secret_access_key,
    default: values.is_default,
  };
}

const EMPTY_VALUES: StorageAccessFormValues = {
  label: "",
  bucket: "",
  region: "",
  host: "",
  access_key_id: "",
  secret_access_key: "",
  is_default: false,
};

const SELECTABLE_PROVIDERS: {
  kind: StorageProviderKind;
  labelKey: string;
  hintKey: string;
}[] = [
  {
    kind: "s3",
    labelKey: "storageAccess.providers.amazonS3",
    hintKey: "storageAccess.providers.amazonS3Hint",
  },
  {
    kind: "s3_compatible",
    labelKey: "storageAccess.providers.s3Compatible",
    hintKey: "storageAccess.providers.s3CompatibleHint",
  },
];

const DISABLED_PROVIDERS: string[] = [
  "storageAccess.providers.googleDrive",
  "storageAccess.providers.dropbox",
];

const TEST_INDICATORS: Record<
  "ok" | "error",
  { textClassName: string; dotClassName: string; labelKey: string }
> = {
  ok: {
    textClassName: "text-status-active",
    dotClassName: "bg-status-active",
    labelKey: "storageAccess.form.connectionOk",
  },
  error: {
    textClassName: "text-status-error",
    dotClassName: "bg-status-error",
    labelKey: "storageAccess.form.connectionError",
  },
};

interface StorageAccessFieldsProps {
  provider: StorageProviderKind;
  isCreate: boolean;
  onTest: () => Promise<void>;
}

function StorageAccessFields({
  provider,
  isCreate,
  onTest,
}: StorageAccessFieldsProps): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const { trigger } = useFormContext<StorageAccessFormValues>();
  const [testState, setTestState] = useState<
    "idle" | "testing" | "ok" | "error"
  >("idle");

  // Tests the credentials currently entered in the form (create draft, or the
  // unsaved edits of an existing record), so validate first — the test needs a
  // complete, valid set (the stored secret is reused when the field is empty).
  async function handleTest(): Promise<void> {
    const valid = await trigger();
    if (!valid) return;
    setTestState("testing");
    try {
      await onTest();
      setTestState("ok");
    } catch {
      setTestState("error");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        {t("storageAccess.form.subtitle")}
      </p>
      <TextField<StorageAccessFormValues>
        label={t("storageAccess.form.label")}
        name="label"
        placeholder={t("storageAccess.form.labelPlaceholder")}
      />
      <TextField<StorageAccessFormValues>
        label={t("storageAccess.form.bucket")}
        name="bucket"
        placeholder="s3://mon-bucket"
      />
      <div className="grid grid-cols-2 gap-4">
        <TextField<StorageAccessFormValues>
          label={t("storageAccess.form.region")}
          name="region"
          placeholder="eu-west-3"
        />
        <TextField<StorageAccessFormValues>
          label={t("storageAccess.form.host")}
          name="host"
          placeholder={
            provider === "s3_compatible"
              ? "s3.exemple.com"
              : t("storageAccess.form.hostOptional")
          }
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TextField<StorageAccessFormValues>
          label={t("storageAccess.form.accessKey")}
          name="access_key_id"
          placeholder="AKIA…"
        />
        <TextField<StorageAccessFormValues>
          label={t("storageAccess.form.secretKey")}
          name="secret_access_key"
          type="password"
          placeholder={
            isCreate
              ? "••••••••"
              : t("storageAccess.form.secretPlaceholderEdit")
          }
        />
      </div>
      <SwitchField<StorageAccessFormValues>
        label={t("storageAccess.form.isDefault")}
        name="is_default"
      />
      <div className="flex items-center gap-3">
        <Button
          disabled={testState === "testing"}
          type="button"
          variant="outline"
          onClick={() => {
            handleTest().catch(() => undefined);
          }}
        >
          {t("storageAccess.form.test")}
        </Button>
        {testState === "testing" ? (
          <span className="text-muted-foreground text-sm">…</span>
        ) : null}
        {testState === "ok" || testState === "error" ? (
          <span
            className={cn(
              "flex items-center gap-1.5 text-sm",
              TEST_INDICATORS[testState].textClassName,
            )}
          >
            <span
              className={cn(
                "size-1.5 rounded-full",
                TEST_INDICATORS[testState].dotClassName,
              )}
            />
            {t(TEST_INDICATORS[testState].labelKey)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

interface StorageAccessFormDialogProps {
  open: boolean;
  access: StorageAccess | null;
  onClose: () => void;
}

export default function StorageAccessFormDialog({
  open,
  access,
  onClose,
}: StorageAccessFormDialogProps): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const {
    createStorageAccess,
    updateStorageAccess,
    testStorageAccessCredentials,
    isSaving,
  } = useStorageAccessMutations();

  const isCreate = access === null;
  // step/provider are derived from `open`/`access` by the effect below (the
  // dialog stays mounted, so these initializers only ever run once, with the
  // create defaults).
  const [step, setStep] = useState<"provider" | "form">("provider");
  const [provider, setProvider] = useState<StorageProviderKind>("s3");

  useEffect(() => {
    if (!open) return;
    if (access) {
      setProvider(access.type);
      setStep("form");
    } else {
      setProvider("s3");
      setStep("provider");
    }
  }, [open, access]);

  const schema = useMemo(
    () => buildStorageAccessSchema(provider, isCreate),
    [provider, isCreate],
  );

  const methods = useForm<StorageAccessFormValues>({
    defaultValues: EMPTY_VALUES,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (open) {
      methods.reset(
        access
          ? {
              label: access.label,
              bucket: access.bucket_url,
              region: access.region,
              host: access.host ?? "",
              access_key_id: access.access_key,
              secret_access_key: "",
              is_default: access.default,
            }
          : EMPTY_VALUES,
      );
    }
  }, [open, access, methods]);

  const onValid = async (values: StorageAccessFormValues): Promise<void> => {
    const payload = buildPayload(values, provider);

    try {
      if (access) {
        await updateStorageAccess({ uuid: access.uuid, data: payload });
      } else {
        await createStorageAccess(payload);
      }
      onClose();
    } catch (error) {
      const applied = applyApiFieldErrors(
        error,
        methods.setError,
        API_FIELD_MAP,
        (field, message) => translateFieldError(field, message, t),
      );
      if (!applied) toast.error(t("editor-notif:saveError"));
    }
  };

  // Everything the single (always-mounted) Dialog needs, gathered per step —
  // one lookup instead of a separate `step` ternary on each Dialog prop, and
  // only the active step's content is built. Remounting a distinct Dialog per
  // step would flash (close then open).
  const stepView =
    step === "provider"
      ? {
          title: t("storageAccess.wizard.step1Title"),
          description: t("storageAccess.wizard.step1Subtitle"),
          confirmLabel: t("storageAccess.wizard.continue"),
          isConfirmDisabled: false,
          onConfirm: (): void => setStep("form"),
          content: (
            <div className="grid grid-cols-2 gap-3">
              {SELECTABLE_PROVIDERS.map((item) => (
                <button
                  key={item.kind}
                  onClick={() => setProvider(item.kind)}
                  type="button"
                  className={cn(
                    "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                    provider === item.kind
                      ? "border-primary bg-primary/10"
                      : "border-border hover:bg-accent",
                  )}
                >
                  <ProviderIcon provider={item.kind} />
                  <div>
                    <p className="font-medium">{t(item.labelKey)}</p>
                    <p className="text-muted-foreground text-sm">
                      {t(item.hintKey)}
                    </p>
                  </div>
                </button>
              ))}
              {DISABLED_PROVIDERS.map((labelKey) => (
                <div
                  key={labelKey}
                  className="border-border flex items-start gap-3 rounded-lg border border-dashed p-3 opacity-50"
                >
                  <div>
                    <p className="font-medium">{t(labelKey)}</p>
                    <p className="text-muted-foreground text-sm">
                      {t("storageAccess.wizard.comingSoon")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ),
        }
      : {
          title: access
            ? t("storageAccess.wizard.editTitle")
            : t(
                `storageAccess.providers.${provider === "s3" ? "amazonS3" : "s3Compatible"}`,
              ),
          description: undefined,
          confirmLabel: t("editor-offers:save"),
          isConfirmDisabled: isSaving,
          onConfirm: methods.handleSubmit(onValid),
          content: (
            <StorageAccessFields
              isCreate={isCreate}
              provider={provider}
              onTest={async (): Promise<void> => {
                await testStorageAccessCredentials(
                  buildPayload(methods.getValues(), provider),
                  access?.uuid,
                );
              }}
            />
          ),
        };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <Dialog
        cancelLabel={t("editor-offers:cancel")}
        confirmLabel={stepView.confirmLabel}
        contentClassName="sm:max-w-lg"
        description={stepView.description}
        isConfirmDisabled={stepView.isConfirmDisabled}
        isOpen={open}
        onConfirm={stepView.onConfirm}
        onOpenChange={(next) => !next && onClose()}
        title={stepView.title}
      >
        {stepView.content}
      </Dialog>
    </FormProvider>
  );
}
